import { useState, useEffect, useCallback } from 'react'
import { Youtube, Save } from 'lucide-react'
import { useAuth } from '../../context/AuthContext'

// YouTube-only content control — per platform × per type, delivered to apps via watch /api/config
// AND enforced server-side in the watch feeds (/api/vod OnDemand filter + /api/v2/live Live strip).
// SAFETY: Live defaults PRESERVE current behavior (web/firetv/mobile ON, roku OFF); OnDemand default OFF.
// See tenbandtv-docs/dev/YOUTUBE_ONLY_SUPPORT.md.
const BASE_DEFAULTS = {
  youtube_web_live: '1', youtube_web_ondemand: '0',
  youtube_firetv_live: '1', youtube_firetv_ondemand: '0',
  youtube_mobile_live: '1', youtube_mobile_ondemand: '0',
  youtube_roku_live: '0', youtube_roku_ondemand: '0',
  youtube_ondemand_mode: 'embed',
  // Hand-off leave-confirm modal (web On-Demand, handoff mode). Empty title/body → app-config default copy.
  youtube_leave_modal_enabled: '1',
  youtube_leave_modal_title: '',
  youtube_leave_modal_body: '',
}
const MANAGED_KEYS = Object.keys(BASE_DEFAULTS)

const PLATFORMS = [
  { id: 'web', label: 'Web' },
  { id: 'firetv', label: 'Fire TV' },
  { id: 'mobile', label: 'Mobile (Apple / Android)' },
  { id: 'roku', label: 'Roku', note: 'Roku cannot play YouTube — leave OFF.' },
]

export default function YouTubeSettings() {
  const { api } = useAuth()
  const [settings, setSettings] = useState({ ...BASE_DEFAULTS })
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [status, setStatus] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const data = await api('/settings?prefix=youtube_')
      const s = data.settings || {}
      setSettings(prev => ({ ...prev, ...Object.fromEntries(Object.entries(s).filter(([, v]) => v !== null && v !== '')) }))
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    } finally {
      setLoading(false)
    }
  }, [api])

  useEffect(() => { load() }, [load])

  const on = (key) => settings[key] === '1'
  const toggle = (key) => setSettings(prev => ({ ...prev, [key]: prev[key] === '1' ? '0' : '1' }))

  async function save() {
    setSaving(true); setStatus('Saving…')
    try {
      const payload = {}
      for (const k of MANAGED_KEYS) payload[k] = String(settings[k] ?? BASE_DEFAULTS[k])
      await api('/settings', { method: 'PUT', body: JSON.stringify({ settings: payload }) })
      setStatus('✅ Saved. Apps reflect changes within ~5 min (/api/config cache).')
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    } finally {
      setSaving(false)
    }
  }

  const cardStyle = { background: 'var(--color-secondary)', border: '1px solid var(--color-border)', borderRadius: 8, padding: 20, marginBottom: 16 }
  const Toggle = ({ k }) => (
    <button type="button" role="switch" aria-checked={on(k)} onClick={() => toggle(k)}
      style={{
        minWidth: 62, padding: '5px 10px', fontSize: 12, fontWeight: 600, borderRadius: 6, cursor: 'pointer',
        background: on(k) ? 'var(--color-primary, #dc2626)' : 'transparent',
        color: on(k) ? '#fff' : 'var(--color-text-muted)',
        border: '1px solid ' + (on(k) ? 'transparent' : 'var(--color-border)'),
      }}>
      {on(k) ? 'ON' : 'OFF'}
    </button>
  )

  const ytMode = settings.youtube_ondemand_mode === 'handoff' ? 'handoff' : 'embed'

  return (
    <div style={{ maxWidth: 720, padding: 24 }}>
      <h1 style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 22, margin: '0 0 4px' }}>
        <Youtube size={22} /> YouTube Settings
      </h1>
      <p style={{ color: 'var(--color-text-muted)', fontSize: 13, margin: '0 0 20px', lineHeight: 1.5 }}>
        Control whether YouTube-only <strong>Live</strong> events and <strong>On-Demand</strong> videos are delivered to
        each platform. Enforced in the watch feeds (Roku/Fire TV/Apple/Android never receive YouTube they can't play).
        Changes reach apps within ~5 min.
      </p>

      <div style={cardStyle}>
        {loading ? <div style={{ color: 'var(--color-text-muted)' }}>Loading…</div> : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ textAlign: 'left', color: 'var(--color-text-muted)', fontSize: 12 }}>
                <th style={{ padding: '6px 8px' }}>Platform</th>
                <th style={{ padding: '6px 8px', width: 90, textAlign: 'center' }}>YT Live</th>
                <th style={{ padding: '6px 8px', width: 110, textAlign: 'center' }}>YT On-Demand</th>
              </tr>
            </thead>
            <tbody>
              {PLATFORMS.map(p => (
                <tr key={p.id} style={{ borderTop: '1px solid var(--color-border)' }}>
                  <td style={{ padding: '10px 8px' }}>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{p.label}</div>
                    {p.note && <div style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>{p.note}</div>}
                  </td>
                  <td style={{ padding: '10px 8px', textAlign: 'center' }}><Toggle k={`youtube_${p.id}_live`} /></td>
                  <td style={{ padding: '10px 8px', textAlign: 'center' }}><Toggle k={`youtube_${p.id}_ondemand`} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div style={cardStyle}>
        <h3 style={{ fontSize: 15, fontWeight: 600, margin: '0 0 8px' }}>Web On-Demand playback</h3>
        <p style={{ color: 'var(--color-text-muted)', fontSize: 12, margin: '0 0 12px' }}>
          How a YouTube-only on-demand video plays on the web (when Web → YT On-Demand is ON).
        </p>
        <div style={{ display: 'inline-flex', border: '1px solid var(--color-border)', borderRadius: 6, overflow: 'hidden' }}>
          {[{ v: 'embed', label: 'In-app embed' }, { v: 'handoff', label: 'Hand-off to YouTube' }].map(o => (
            <button key={o.v} type="button" onClick={() => setSettings(prev => ({ ...prev, youtube_ondemand_mode: o.v }))}
              style={{
                padding: '7px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer', border: 'none',
                background: ytMode === o.v ? 'var(--color-primary, #dc2626)' : 'transparent',
                color: ytMode === o.v ? '#fff' : 'var(--color-text)',
              }}>
              {o.label}
            </button>
          ))}
        </div>
      </div>

      <div style={cardStyle}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 4 }}>
          <div>
            <h3 style={{ fontSize: 15, fontWeight: 600, margin: '0 0 2px' }}>Hand-off leave prompt</h3>
            <p style={{ color: 'var(--color-text-muted)', fontSize: 12, margin: 0, lineHeight: 1.5 }}>
              Confirmation modal shown before leaving for YouTube — Web On-Demand <strong>Hand-off</strong> mode only.
              In-app embed plays without it.
            </p>
          </div>
          <Toggle k="youtube_leave_modal_enabled" />
        </div>
        <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--color-text-muted)', margin: '12px 0 4px' }}>Title</label>
        <input type="text" value={settings.youtube_leave_modal_title || ''}
          onChange={(e) => setSettings(prev => ({ ...prev, youtube_leave_modal_title: e.target.value }))}
          placeholder="Watch on YouTube?"
          style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', fontSize: 13, borderRadius: 6, border: '1px solid var(--color-border)', background: 'var(--color-bg, #0b1220)', color: 'var(--color-text)' }} />
        <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--color-text-muted)', margin: '12px 0 4px' }}>Body</label>
        <textarea rows={3} value={settings.youtube_leave_modal_body || ''}
          onChange={(e) => setSettings(prev => ({ ...prev, youtube_leave_modal_body: e.target.value }))}
          placeholder="{title} is available on YouTube. Watching it will leave TenbandTV and open YouTube in a new tab."
          style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', fontSize: 13, borderRadius: 6, border: '1px solid var(--color-border)', background: 'var(--color-bg, #0b1220)', color: 'var(--color-text)', resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.5 }} />
        <p style={{ color: 'var(--color-text-muted)', fontSize: 11, margin: '6px 0 0' }}>
          Use <code>{'{title}'}</code> to insert the video title. Leave blank to use the default copy.
        </p>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button onClick={save} disabled={saving || loading}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'var(--color-primary, #dc2626)', color: '#fff', border: 'none', borderRadius: 6, padding: '9px 16px', fontSize: 13, fontWeight: 600, cursor: saving || loading ? 'default' : 'pointer', opacity: saving || loading ? 0.6 : 1 }}>
          <Save size={15} /> {saving ? 'Saving…' : 'Save'}
        </button>
        <span style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>{status}</span>
      </div>
    </div>
  )
}
